import React from "react";
import { Box, Typography, Button, Grid, Paper, Chip, Divider } from "@mui/material";
import { BsArrowDownCircleFill, BsPlayCircleFill, BsCalendarCheck, BsCameraVideoFill, BsFilm } from "react-icons/bs";

function InfoRow({ icon, label, value }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", py: 1.2, borderBottom: "1px solid #000000" }}>
            <Box sx={{ color: "#E0B721", mr: 1.5, mt: 0.3, fontSize: 18 }}>
                {icon}
            </Box>
            <Box sx={{ flexGrow: 1 }} className="text">
                <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, display: "block", opacity: 0.7 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.2, wordBreak: "break-word" }}>
                    {value || "N/A"}
                </Typography>
            </Box>
        </Box>
    );
}

export default function VideoPlayer({ data, download_name = Date().toString(), onClose }) {
    const videoList = data?.video_urls || [];
    const videoRef = React.useRef(null);
    const [currentIndex, setCurrentIndex] = React.useState(0);

    React.useEffect(() => {
        setCurrentIndex(0);
    }, [data?.id]);

    const handleVideoEnd = () => {
        if (currentIndex < videoList.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setTimeout(() => {
                try {
                    videoRef.current?.play();
                } catch (err) {
                    console.log("Autoplay check:", err);
                }
            }, 100);
        }
    };

    const handleDownload = () => {
        const url = videoList?.[currentIndex] || videoList?.[0];
        if (!url) return;
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${data?.name || "timelapse"}-${currentIndex + 1}.mp4`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box className="text" sx={{
            height: { xs: "auto", md: "calc(100vh - 68px)" },
            maxHeight: { md: "calc(100vh - 68px)" },
            overflow: { xs: "auto", md: "hidden" },
            p: { xs: 1, md: 2 },
            display: "flex",
            flexDirection: "column"
        }}>
            <Grid container spacing={3} sx={{ flexGrow: 1, height: { md: "100%" }, flexWrap: { xs: "wrap", md: "nowrap" } }}>
                {/* Left Cinema Player Area */}
                <Grid item xs={12} md={8} lg={9} sx={{ height: { md: "100%" }, display: "flex", flexDirection: "column", width: { xs: "100%", md: "66%", lg: "75%" } }}>
                    <Box sx={{
                        background: "#000000",
                        borderRadius: 8,
                        overflow: "hidden",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                        border: "2px solid #000000",
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: { xs: 380, sm: 460, md: "100%" },
                        minHeight: { xs: 380, md: "auto" },
                        position: "relative"
                    }}>
                        {videoList?.length > 0 && videoList[currentIndex] ? (
                            <video
                                ref={videoRef}
                                width="100%"
                                height="100%"
                                controls
                                autoPlay
                                style={{ width: "100%", height: "100%", maxHeight: "100%", objectFit: "contain", outline: "none", borderRadius: "6px" }}
                                onEnded={handleVideoEnd}
                                key={videoList[currentIndex]}
                            >
                                <source src={videoList[currentIndex]} type="video/mp4" />
                                Your browser does not support HTML5 video.
                            </video>
                        ) : (
                            <Box sx={{ p: 6, textAlign: "center", opacity: 0.8, color: "#fff" }}>
                                <BsFilm style={{ fontSize: 56, marginBottom: 16 }} />
                                <Typography variant="h6" gutterBottom>
                                    No Video URL Available
                                </Typography>
                                <Typography variant="body2">
                                    {data?.status === "processing"
                                        ? "The timelapse video is currently being processed by the server."
                                        : "No video clip could be generated or found for this date range."}
                                </Typography>
                            </Box>
                        )}

                        {/* Playlist navigation badge if multiple clips */}
                        {videoList?.length > 1 && (
                            <Box sx={{
                                position: "absolute",
                                top: 16,
                                left: 16,
                                background: "rgba(0,0,0,0.75)",
                                borderRadius: 5,
                                px: 2,
                                py: 0.6,
                                border: "1px solid #000000",
                                display: "flex",
                                alignItems: "center",
                                gap: 1
                            }}>
                                <Typography variant="caption" sx={{ color: "#E0B721", fontWeight: 700 }}>
                                    CLIP {currentIndex + 1} OF {videoList.length}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Multi-clip selector thumbnails/buttons if applicable */}
                    {videoList?.length > 1 && (
                        <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, overflowX: "auto", flexShrink: 0, pb: 0.5 }}>
                            {videoList.map((url, idx) => (
                                <Button
                                    key={idx}
                                    onClick={() => setCurrentIndex(idx)}
                                    className={currentIndex === idx ? "btn-primary" : ""}
                                    variant={currentIndex === idx ? "contained" : "outlined"}
                                    size="small"
                                    sx={{
                                        borderRadius: 2,
                                        border: "1px solid #000000",
                                        fontWeight: 600,
                                        textTransform: "none"
                                    }}
                                >
                                    Part {idx + 1}
                                </Button>
                            ))}
                        </Box>
                    )}
                </Grid>

                {/* Right Info & Action Sidebar */}
                <Grid item xs={12} md={4} lg={3} sx={{ height: { md: "100%" }, width: { xs: "100%", md: "34%", lg: "25%" } }}>
                    <Paper className="card-bg1 shadow text" sx={{
                        borderRadius: 8,
                        p: 2.5,
                        border: "2px solid #000000",
                        height: { md: "100%" },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        overflowY: "auto"
                    }}>
                        <Box>
                            {/* Status and Duration */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                                <Chip
                                    label={data?.status ? data.status.toUpperCase() : "UNKNOWN"}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: 11,
                                        letterSpacing: 0.8,
                                        backgroundColor: data?.status === "completed" ? "rgba(34, 197, 94, 0.2)" : "rgba(234, 179, 8, 0.2)",
                                        color: data?.status === "completed" ? "#22c55e" : "#eab308",
                                        border: `1px solid ${data?.status === "completed" ? "#22c55e" : "#eab308"}`
                                    }}
                                />
                                {data?.video_duration > 0 && (
                                    <Chip
                                        label={`${data.video_duration} min target`}
                                        size="small"
                                        sx={{ border: "1px solid #000000", fontSize: 11 }}
                                    />
                                )}
                            </Box>

                            <Typography variant="h6" className="text" sx={{ fontWeight: 700, mb: 1.5, lineHeight: 1.3 }}>
                                {data?.name || "Timelapse Video"}
                            </Typography>

                            <Divider sx={{ borderColor: "#000000", mb: 1 }} />

                            {/* Info Rows */}
                            <InfoRow
                                icon={<BsCameraVideoFill />}
                                label="Camera"
                                value={data?.camera_name || data?.camera || "Primary Camera"}
                            />
                            <InfoRow
                                icon={<BsCalendarCheck />}
                                label="Date From"
                                value={data?.date_from || "N/A"}
                            />
                            <InfoRow
                                icon={<BsCalendarCheck />}
                                label="Date To"
                                value={data?.date_to || "N/A"}
                            />
                            <InfoRow
                                icon={<BsPlayCircleFill />}
                                label="Total Frames Merged"
                                value={data?.frame_count ? `${data.frame_count} Images` : "0 Frames"}
                            />
                            <InfoRow
                                icon={<BsFilm />}
                                label="Created At"
                                value={data?.created_at ? new Date(data.created_at).toLocaleString() : "Recent"}
                            />
                        </Box>

                        {/* Download & Actions Area */}
                        <Box sx={{ mt: 3, pt: 2, borderTop: "2px solid #000000" }}>
                            <Button
                                fullWidth
                                variant="contained"
                                className="btn-primary"
                                disabled={!videoList || videoList.length === 0}
                                onClick={handleDownload}
                                sx={{
                                    py: 1.2,
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    fontSize: 14,
                                    textTransform: "none",
                                    border: "1px solid #000000",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    "&:disabled": {
                                        opacity: 0.5
                                    }
                                }}
                            >
                                <BsArrowDownCircleFill style={{ fontSize: 18, marginRight: 10 }} />
                                Download MP4 Video
                            </Button>

                            {onClose && (
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={onClose}
                                    sx={{
                                        mt: 1,
                                        py: 1,
                                        borderRadius: 2,
                                        borderColor: "#000000",
                                        color: "inherit",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        "&:hover": {
                                            borderColor: "#000000",
                                            opacity: 0.8
                                        }
                                    }}
                                >
                                    Close Player
                                </Button>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}